import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiPost, apiPut, SessionExpiredError } from '../../lib/api'
import { InlineError } from '../InlineError'
import PoHeaderDialog from './PoHeaderDialog'
import ExportDialog from './ExportDialog'
import PoLoader, { type LoaderState } from './PoLoader'
import { broadcast, popoutName, useMultiPoSync, withQty } from './sync'
import { PO_TINTS, PRODUCT_FIELDS, SELECT_FILTER_FIELDS, StyleBlock, searchText } from './MultiPoGrid'
import type { CellState, ColumnGroup, FieldKey, ProductTotals, SelectFilterField } from './MultiPoGrid'

// ── API types (docs/BossMultiPO.md §4.2) ─────────────────────────────
export type MultiStore = { storeId: string; storeNumber: string | null; name: string; city: string | null }

export type PoListRow = {
    poGuid: string; poNumber: string; label: string; catalogGuid: string
    shipDate: string | null; preShipLockdownDate: string | null; manualLockdown: boolean
    isGroup: boolean; storeGuid: string | null; storeNumber: string | null; storeName: string | null
    season: string | null; supplierName: string; units: number
    locked: boolean; lockReason: string
    /** Owned by a store outside the user's allowed stores: it loads read-only. */
    readOnly?: boolean
}

export type LoadedPo = {
    poGuid: string; poNumber: string; label: string; isGroup: boolean
    storeGuid: string | null; storeNumber: string | null; storeName: string | null
    shipDate: string | null; preShipLockdownDate: string | null; lockMoment: string | null
    manualLockdown: boolean; restricted: boolean; locked: boolean; lockReason: string
}

export type LoadedItem = {
    catalogItemGuid: string; itemCardId: number; styleCode: string | null; description: string
    cost: number | null; retail: number | null; retailMissing: boolean
    prodNo: string | null; itemNumber: string | null; styleCodes: string | null; season: string | null
    grp1: string | null; grp2: string | null; grp3: string | null; comment2: string | null; pmNotes: string | null
    estLanded: number | null; mecMatched: boolean
    colors: { colorId: number; name: string | null }[]
    sizes: { sizeId: number; name: string | null }[]
}

export type LoadResult = {
    store: string
    catalog: { catalogGuid: string; season: string | null; supplierName: string | null }
    pos: LoadedPo[]
    items: LoadedItem[]
    /** poGuid → "catalogItemGuid|colorId|sizeId" → quantity */
    quantities: Record<string, Record<string, number>>
    /** "itemCardId|colorId|sizeId" → on hand at the selected store */
    onHand: Record<string, number>
}

type View = 'bypo' | 'bysize' | 'perpo'

const fmtDate = (s: string | null) => (s ? s.slice(0, 10) : '—')
export const cellKey = (catalogItemGuid: string, colorId: number, sizeId: number) =>
    `${catalogItemGuid.toLowerCase()}|${colorId}|${sizeId}`

/**
 * Load POs mode: pick POs in the PO Loader, view them in the colour × size grid. The store the
 * user works as, the Loader's place, the loaded POs and the view live in the URL
 * (`store=&t=&sup=&sea=&os=&pos=a,b&view=bysize|perpo`) so a refresh, a link or a pop-out lands
 * on the same screen.
 */
export default function LoadPos({ token, writesEnabled, popout = false }: { token: string | null; writesEnabled: boolean; popout?: boolean }) {
    const [params, setParams] = useSearchParams()
    useMultiPoSync(useQueryClient())
    const store = params.get('store') ?? ''
    const loadedGuids = useMemo(() => (params.get('pos') ?? '').split(',').filter(Boolean), [params])
    const viewParam = params.get('view')
    const view: View = viewParam === 'perpo' || viewParam === 'bysize' ? viewParam : 'bypo'
    const loader: LoaderState = {
        type: params.get('t') === 'group' ? 'group' : 'direct',
        supplier: params.get('sup'),
        season: params.get('sea'),
        ownerStore: params.get('os'),
    }

    const update = (changes: Record<string, string | null>) => {
        const next = new URLSearchParams(params)
        for (const [k, v] of Object.entries(changes)) {
            if (v) next.set(k, v)
            else next.delete(k)
        }
        setParams(next, { replace: true })
    }

    const storesQuery = useQuery<MultiStore[]>({
        queryKey: ['multipo-stores'],
        queryFn: () => api('/api/purchase-orders/multi/stores', token),
    })
    const stores = storesQuery.data ?? []

    // Default to the first store the user may act for.
    useEffect(() => {
        if (!store && stores.length > 0) update({ store: stores[0].storeId })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store, stores])

    return (
        <>
            <div className="po-panel po-toolbar-panel">
                <div className="po-toolbar">
                    <div className="form-field">
                        <label className="form-label" title="On hand and Group PO shares are this store's">Working as</label>
                        <select className="form-input" value={store}
                            onChange={e => update({ store: e.target.value })}
                            disabled={storesQuery.isLoading}>
                            {stores.length === 0 && <option value="">{storesQuery.isLoading ? 'Loading stores…' : 'No stores'}</option>}
                            {stores.map(s => (
                                <option key={s.storeId} value={s.storeId}>{s.storeNumber} — {s.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {storesQuery.isError && <InlineError message="Failed to load your stores." />}

            {store && (loadedGuids.length === 0
                ? <PoLoader token={token} store={store} stores={stores} state={loader} writesEnabled={writesEnabled}
                    onState={st => update({ t: st.type === 'group' ? 'group' : null, sup: st.supplier, sea: st.season, os: st.ownerStore })}
                    onActingStore={id => update({ store: id })}
                    onLoad={guids => update({ pos: guids.join(',') })} />
                : <LoadedGrid token={token} store={store} poGuids={loadedGuids} view={view} writesEnabled={writesEnabled} popout={popout}
                    onView={v => update({ view: v === 'bypo' ? null : v })}
                    onClose={() => update({ pos: null })} />)}
        </>
    )
}

// ── Loaded grid ───────────────────────────────────────────────────────

const FIELDS_KEY = 'multipo.productFields'
const ALL_FIELDS = PRODUCT_FIELDS.map(f => f.key)

/** The product values the user chose to show, remembered per browser. */
function useProductFields(): [FieldKey[], (keys: FieldKey[]) => void] {
    const [fields, setFields] = useState<FieldKey[]>(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(FIELDS_KEY) ?? 'null')
            if (Array.isArray(saved)) return ALL_FIELDS.filter(k => saved.includes(k))
        } catch { /* fall through to the default */ }
        return ALL_FIELDS
    })
    const save = (keys: FieldKey[]) => {
        setFields(keys)
        try { localStorage.setItem(FIELDS_KEY, JSON.stringify(keys)) } catch { /* not persisted */ }
    }
    return [fields, save]
}

function LoadedGrid({ token, store, poGuids, view, writesEnabled, popout, onView, onClose }: {
    token: string | null; store: string; poGuids: string[]; view: View; writesEnabled: boolean; popout: boolean
    onView: (v: View) => void; onClose: () => void
}) {
    const queryClient = useQueryClient()
    const [filter, setFilter] = useState('')
    const [selectFilters, setSelectFilters] = useState<Partial<Record<SelectFilterField, string>>>({})
    const [withQtyOnly, setWithQtyOnly] = useState(false)
    const [showOnHand, setShowOnHand] = useState(true)
    const [fields, setFields] = useProductFields()
    const [cellStates, setCellStates] = useState<Record<string, CellState>>({})
    const [editing, setEditing] = useState<string | null>(null)
    const [exporting, setExporting] = useState<string | null>(null)

    const queryKey = useMemo(() => ['multipo-load', store, poGuids.join(',')], [store, poGuids])
    const loadQuery = useQuery<LoadResult>({
        queryKey,
        queryFn: () => apiPost('/api/purchase-orders/multi/load', token, { store, poGuids }),
    })
    const data = loadQuery.data

    // A pop-out is named after its POs, so the windows can be told apart in the taskbar.
    useEffect(() => {
        if (!popout || !data) return
        document.title = `${data.pos.map(p => p.label).join(', ')} · BOSS MultiPO`
    }, [popout, data])

    /** Writes one cell's quantity into the cached load, so every view and total follows it. */
    const patchQty = useCallback((poGuid: string, key: string, qty: number) => {
        queryClient.setQueryData<LoadResult>(queryKey, old => withQty(old, poGuid, key, qty))
    }, [queryClient, queryKey])

    // Saves always go out with the current session; one refused while it was over waits here and
    // goes again, with its original oldQty so the stale check still holds, once the user is back.
    const tokenRef = useRef(token)
    tokenRef.current = token
    type PendingSave = { po: LoadedPo; item: LoadedItem; colorId: number; sizeId: number; oldQty: number; newQty: number }
    const pendingRef = useRef(new Map<string, PendingSave>())

    const setCell = (k: string, st: CellState | null) =>
        setCellStates(prev => {
            const next = { ...prev }
            if (st) next[k] = st
            else delete next[k]
            return next
        })

    const save = useCallback(async (po: LoadedPo, item: LoadedItem, colorId: number, sizeId: number, oldQty: number, newQty: number) => {
        const key = cellKey(item.catalogItemGuid, colorId, sizeId)
        const stateKey = `${po.poGuid}|${key}`
        setCell(stateKey, { state: 'saving' })
        patchQty(po.poGuid, key, newQty)
        try {
            const res: { qty: number } = await apiPut(`/api/purchase-orders/multi/pos/${po.poGuid}/items`, tokenRef.current,
                { store, catalogItemGuid: item.catalogItemGuid, colorId, sizeId, oldQty, newQty })
            patchQty(po.poGuid, key, res.qty)
            broadcast({ type: 'item-saved', store, poGuid: po.poGuid, key, qty: res.qty })
            setCell(stateKey, { state: 'saved' })
            setTimeout(() => setCellStates(prev => {
                if (prev[stateKey]?.state !== 'saved') return prev
                const next = { ...prev }
                delete next[stateKey]
                return next
            }), 1500)
        } catch (err) {
            if (err instanceof SessionExpiredError) {
                pendingRef.current.set(stateKey, { po, item, colorId, sizeId, oldQty, newQty })
                setCell(stateKey, { state: 'saving', message: 'Waiting for you to sign in again.' })
                return
            }
            const body = errorBody(err)
            if (typeof body?.currentQty === 'number') {
                // Someone else saved this cell first: show their number; the user re-enters if needed.
                patchQty(po.poGuid, key, body.currentQty)
                setCell(stateKey, { state: 'conflict', message: `Changed by someone else to ${body.currentQty}. Enter your number again if you still want it.` })
            } else {
                patchQty(po.poGuid, key, oldQty)
                setCell(stateKey, { state: 'error', message: body?.error ?? 'Not saved. Try again.' })
            }
        }
    }, [store, patchQty])

    // Back from an expired session: send what was waiting.
    useEffect(() => {
        if (!token || pendingRef.current.size === 0) return
        const waiting = [...pendingRef.current.values()]
        pendingRef.current.clear()
        for (const p of waiting) void save(p.po, p.item, p.colorId, p.sizeId, p.oldQty, p.newQty)
    }, [token, save])

    const shortLabels = useMemo(() => distinctTails((data?.pos ?? []).map(p => p.poNumber)), [data])

    const poGroups: ColumnGroup[] = useMemo(() => (data?.pos ?? []).map((po, i) => ({
        key: po.poGuid,
        label: po.label,
        short: shortLabels[i],
        sub: fmtDate(po.shipDate),
        locked: po.locked,
        lockReason: po.lockReason,
        tint: i,
        editable: writesEnabled && !po.locked,
        qty: (item, c, s) => data?.quantities[po.poGuid]?.[cellKey(item.catalogItemGuid, c, s)] ?? 0,
        onSave: (item, c, s, oldQty, newQty) => { void save(po, item, c, s, oldQty, newQty) },
        cellState: (item, c, s) => cellStates[`${po.poGuid}|${cellKey(item.catalogItemGuid, c, s)}`],
    })), [data, writesEnabled, save, cellStates, shortLabels])

    const onHandGroup: ColumnGroup = useMemo(() => ({
        key: 'onhand',
        label: 'On hand',
        short: 'inv',
        tint: null,
        editable: false,
        qty: (item, c, s) => data?.onHand[`${item.itemCardId}|${c}|${s}`] ?? 0,
    }), [data])

    const totalsOf = useCallback((item: LoadedItem): ProductTotals => {
        let qty = 0, inv = 0
        for (const c of item.colors) for (const s of item.sizes) {
            for (const g of poGroups) qty += g.qty(item, c.colorId, s.sizeId)
            inv += onHandGroup.qty(item, c.colorId, s.sizeId)
        }
        return { qty, inv }
    }, [poGroups, onHandGroup])

    const selectOptions = useMemo(() => {
        const out = {} as Record<SelectFilterField, string[]>
        for (const f of SELECT_FILTER_FIELDS) {
            out[f] = [...new Set((data?.items ?? []).map(i => i[f]).filter((v): v is string => !!v))]
                .sort((a, b) => a.localeCompare(b))
        }
        return out
    }, [data])

    const items = useMemo(() => {
        const f = filter.trim().toLowerCase()
        return (data?.items ?? []).filter(i =>
            (!f || searchText(i).includes(f)) &&
            SELECT_FILTER_FIELDS.every(k => !selectFilters[k] || i[k] === selectFilters[k]) &&
            (!withQtyOnly || totalsOf(i).qty !== 0))
    }, [data, filter, selectFilters, withQtyOnly, totalsOf])

    if (loadQuery.isLoading) return <div className="empty-state"><p>Loading POs…</p></div>
    if (loadQuery.isError || !data) {
        return (
            <div className="po-panel">
                <InlineError message={errorText(loadQuery.error) ?? 'Failed to load these POs.'} />
                <button className="btn btn-secondary" style={{ marginTop: 'var(--space-3)' }} onClick={onClose}>Back to the PO list</button>
            </div>
        )
    }

    const totalUnits = (g: ColumnGroup) =>
        data.items.reduce((sum, i) => sum + i.colors.reduce((cs, c) => cs + i.sizes.reduce((ss, s) => ss + g.qty(i, c.colorId, s.sizeId), 0), 0), 0)
    const withOnHand = (gs: ColumnGroup[]) => (showOnHand ? [...gs, onHandGroup] : gs)
    const filtersOn = !!filter || Object.values(selectFilters).some(Boolean) || withQtyOnly

    return (
        <>
            {editing && (
                <PoHeaderDialog token={token} store={store} mode={{ kind: 'edit', poGuid: editing }} onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null)
                        queryClient.invalidateQueries({ queryKey: ['multipo-list'] })
                        queryClient.invalidateQueries({ queryKey })
                        broadcast({ type: 'po-changed', poGuid: editing })
                    }} />
            )}
            {exporting && (
                <ExportDialog token={token} store={store} poGuid={exporting} writesEnabled={writesEnabled}
                    onClose={() => setExporting(null)} />
            )}
            <div className="po-panel multipo-loaded-bar">
                <div className="multipo-loaded-title">
                    <strong>{data.catalog.supplierName ?? 'Catalog'}</strong> · {data.catalog.season ?? 'No season'}
                    {!popout && (
                        <button className="btn btn-secondary multipo-btn-sm" title="Open these POs in their own window"
                            onClick={() => openPopout(store, poGuids, view)}>Pop out ↗</button>
                    )}
                </div>
                <div className="multipo-chips">
                    {data.pos.map((po, i) => (
                        <span key={po.poGuid} className={`multipo-chip multipo-tint-${i % PO_TINTS} ${po.locked ? 'locked' : ''}`}
                            title={po.locked ? po.lockReason : po.lockMoment ? `Open until ${po.lockMoment.replace('T', ' ').slice(0, 16)}` : 'Open'}>
                            {po.locked ? '🔒 ' : ''}{po.label} · {fmtDate(po.shipDate)} · {totalUnits(poGroups[i])} units
                            {writesEnabled && (
                                <button className="multipo-chip-edit" title="Edit this PO's header" aria-label={`Edit ${po.label}`}
                                    onClick={() => setEditing(po.poGuid)}>✎</button>
                            )}
                            <button className="multipo-chip-edit" title="Export this PO to Excel" aria-label={`Export ${po.label}`}
                                onClick={() => setExporting(po.poGuid)}>⇩</button>
                        </span>
                    ))}
                </div>
            </div>

            {/* Docks to the top of the page on desktop so the filters stay in reach while scrolling. */}
            <div className="po-panel multipo-filter-bar">
                <div className="multipo-picker-bar">
                    <div className="multipo-modes" role="tablist" aria-label="Grid view">
                        {VIEWS.map(v => (
                            <button key={v.key} role="tab" aria-selected={view === v.key}
                                className={`multipo-mode ${view === v.key ? 'active' : ''}`} onClick={() => onView(v.key)}>{v.label}</button>
                        ))}
                    </div>
                    <input className="form-input" placeholder="Filter by ProdNo, description, item #, style code or notes…"
                        value={filter} onChange={e => setFilter(e.target.value)} />
                    {SELECT_FILTER_FIELDS.map(k => selectOptions[k].length > 0 && (
                        <select key={k} className="form-input multipo-select-filter" value={selectFilters[k] ?? ''}
                            aria-label={fieldLabel(k)}
                            onChange={e => setSelectFilters(f => ({ ...f, [k]: e.target.value || undefined }))}>
                            <option value="">All {fieldLabel(k)}</option>
                            {selectOptions[k].map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    ))}
                    <label className="multipo-check">
                        <input type="checkbox" checked={withQtyOnly} onChange={e => setWithQtyOnly(e.target.checked)} />
                        Only styles with quantities
                    </label>
                    <label className="multipo-check">
                        <input type="checkbox" checked={showOnHand} onChange={e => setShowOnHand(e.target.checked)} />
                        Show on hand
                    </label>
                    {filtersOn && (
                        <button className="btn btn-secondary multipo-btn-sm" onClick={() => { setFilter(''); setSelectFilters({}); setWithQtyOnly(false) }}>Clear filters</button>
                    )}
                    <div style={{ flexGrow: 1 }} />
                    <ColumnChooser fields={fields} onChange={setFields} />
                    <button className="btn btn-secondary" onClick={onClose}>Change POs</button>
                </div>
            </div>

            {items.length === 0 && <div className="empty-state"><p>No styles match.</p></div>}

            {view === 'perpo'
                ? poGroups.map(g => (
                    <div key={g.key} className="multipo-perpo">
                        <h3 className="multipo-perpo-title">{g.locked ? '🔒 ' : ''}{g.label} <span>· ships {g.sub}</span></h3>
                        {items
                            .filter(i => !withQtyOnly || i.colors.some(c => i.sizes.some(s => g.qty(i, c.colorId, s.sizeId) !== 0)))
                            .map(i => <StyleBlock key={i.catalogItemGuid} item={i} groups={withOnHand([g])} layout="bypo" fields={fields} totals={totalsOf(i)} />)}
                    </div>
                ))
                : items.map(i => (
                    <StyleBlock key={i.catalogItemGuid} item={i} groups={withOnHand(poGroups)}
                        layout={view === 'bysize' ? 'bysize' : 'bypo'} fields={fields} totals={totalsOf(i)} />
                ))}
        </>
    )
}

/**
 * Short PO names for the narrow By Size columns: the part after what every loaded PO number
 * shares, cut at a space or dash ("23-26FW ATX-Aug1" and "23-26FW ATX-Jun1" → "Aug1", "Jun1").
 */
/**
 * Opens the loaded POs in a window of their own (docs/BossMultiPO.md §5). The same PO set always
 * gets the same window name, so popping it out again brings that window forward.
 */
function openPopout(store: string, poGuids: string[], view: View) {
    const params = new URLSearchParams({ store, pos: poGuids.join(',') })
    if (view !== 'bypo') params.set('view', view)
    const name = popoutName(store, poGuids)
    const win = window.open(`/purchase-orders/multipo/popout?${params}`, name,
        `popup,width=${Math.min(1600, screen.availWidth)},height=${Math.min(1000, screen.availHeight)}`)
    win?.focus()
}

export function distinctTails(names: string[]): string[] {
    if (names.length < 2) return names
    let prefix = names[0]
    for (const n of names) while (!n.startsWith(prefix)) prefix = prefix.slice(0, -1)
    const cut = Math.max(prefix.lastIndexOf(' '), prefix.lastIndexOf('-')) + 1
    return names.map(n => n.slice(cut) || n)
}

const VIEWS: { key: View; label: string }[] = [
    { key: 'bypo', label: 'Combined (By PO)' },
    { key: 'bysize', label: 'Combined (By Size)' },
    { key: 'perpo', label: 'Per PO' },
]

const fieldLabel = (k: FieldKey) => PRODUCT_FIELDS.find(f => f.key === k)?.label ?? k

/** Turns the product values above each style on and off. */
function ColumnChooser({ fields, onChange }: { fields: FieldKey[]; onChange: (keys: FieldKey[]) => void }) {
    const ref = useRef<HTMLDetailsElement>(null)
    useEffect(() => {
        const close = (e: MouseEvent) => {
            if (ref.current?.open && !ref.current.contains(e.target as Node)) ref.current.open = false
        }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [])
    const toggle = (k: FieldKey) =>
        onChange(fields.includes(k) ? fields.filter(f => f !== k) : ALL_FIELDS.filter(f => f === k || fields.includes(f)))
    return (
        <details ref={ref} className="multipo-columns">
            <summary className="btn btn-secondary">Columns ({fields.length})</summary>
            <div className="multipo-columns-panel">
                <div className="multipo-columns-actions">
                    <button className="btn btn-secondary multipo-btn-sm" onClick={() => onChange(ALL_FIELDS)}>Show all</button>
                    <button className="btn btn-secondary multipo-btn-sm" onClick={() => onChange([])}>Hide all</button>
                </div>
                {PRODUCT_FIELDS.map(f => (
                    <label key={f.key} className="multipo-check" title={f.title}>
                        <input type="checkbox" checked={fields.includes(f.key)} onChange={() => toggle(f.key)} />
                        {f.label}
                    </label>
                ))}
            </div>
        </details>
    )
}

/** The server's JSON error body, when the request failed with one. */
function errorBody(err: unknown): { error?: string; currentQty?: number } | null {
    if (!(err instanceof Error)) return null
    try {
        return JSON.parse(err.message)
    } catch {
        return { error: err.message || undefined }
    }
}

/** The server's JSON error text, when the request failed with one. */
function errorText(err: unknown): string | null {
    if (!(err instanceof Error)) return null
    try {
        const parsed = JSON.parse(err.message)
        return parsed?.error ?? null
    } catch {
        return err.message || null
    }
}
